import Modal from '@/src/Common/components/Modal';
import useShare from '@/src/Share/components/hooks/useShare';
import { PlusIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/src/shadComponents/ui/button';
import { Label } from '@/src/shadComponents/ui/label';
import { Checkbox } from '@/src/shadComponents/ui/checkbox';

export default function MergeSessionsModal({
    photoSessionId,
    sessions,
    onComplete,
}: {
    photoSessionId: string;
    sessions: any[];
    onComplete: () => void;
}) {
    const cancelButtonRef = useRef(null);
    const [isMergingSessions, setIsMergingSessions] = useState(false);
    const { mergeSessions } = useShare();
    const [sourcePhotoSessionIds, setSourcePhotoSessionIds] = useState<
        string[]
    >([]);

    const merge = async () => {
        setIsMergingSessions(true);
        await mergeSessions(photoSessionId, sourcePhotoSessionIds);
        setIsMergingSessions(false);
        toast.success('Sessions merged');
        onComplete();
    };

    return (
        <Modal cancelButtonRef={cancelButtonRef} onDismiss={onComplete}>
            <div>
                <div className="bg-background px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary sm:mx-0 sm:h-10 sm:w-10">
                            <PlusIcon
                                className="h-6 w-6 text-primary-foreground"
                                aria-hidden="true"
                            />
                        </div>
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                            <h3
                                className="text-base font-semibold text-foreground"
                            >
                                Merge Sessions
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-muted-foreground">
                                    Please select the sessions you want to
                                    merge.
                                    <br />
                                    <br /> Merging a total of{' '}
                                    {sourcePhotoSessionIds.length} sessions.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="ml-12 mt-2 p-2">
                    <div>
                        {sessions.map((session) => {
                            if (session.photoSessionId === photoSessionId) {
                                return null;
                            }

                            return (
                                <div key={session.photoSessionId}>
                                    <input
                                        type="checkbox"
                                        className="mr-2"
                                        id={session.photoSessionId}
                                        checked={sourcePhotoSessionIds.includes(
                                            session.photoSessionId,
                                        )}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSourcePhotoSessionIds([
                                                    ...sourcePhotoSessionIds,
                                                    session.photoSessionId,
                                                ]);
                                            } else {
                                                setSourcePhotoSessionIds(
                                                    sourcePhotoSessionIds.filter(
                                                        (id) =>
                                                            id !==
                                                            session.photoSessionId,
                                                    ),
                                                );
                                            }
                                        }}
                                    />
                                    <label htmlFor={session.photoSessionId}>
                                        {session.date} - {session.total}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="bg-muted/50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
                <Button
                    onClick={merge}
                    disabled={isMergingSessions}
                >
                    {isMergingSessions ? 'Merging...' : 'Merge Sessions'}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => onComplete()}
                    ref={cancelButtonRef}
                >
                    Cancel
                </Button>
            </div>
        </Modal>
    );
}
